import { useEffect, useState } from "react";
import { ActionIcon, Box, Button, Checkbox, createStyles, Drawer, Loader, Modal, Select, Text, TextInput, Textarea } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import dayjs from "dayjs";
import {
  IconArrowRight,
  IconBan,
  IconCheck,
  IconChevronDown,
  IconPencil,
  IconPlus,
  IconSend,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { STATUS_LABELS } from "../ServiceRequestsTable";
import ServiceRequestFields, {
  buildConditionContext,
  ClientsField,
  conditionHolds,
  DateField,
  FacilityField,
  IssueCategoryField,
  PriorityField,
  ShiftField,
  StaffField,
  TimeField,
  clientRefName,
  displayRef,
  refId,
  useSrButtonClassNames,
  useSrFieldClassNames,
} from "../ServiceRequestFields";
import { decrypt, isServiceRequestAdmin } from "../../utils/index";
import { getAllStaff } from "../../services/staff";
import { getAllPatientsModal } from "../../services/patient";
import { getSignatureById } from "../../services/signature";
import {
  getBoards,
  getServiceRequestById,
  updateServiceRequest,
  addServiceRequestComment,
  moveServiceRequest,
  approveServiceRequest,
  rejectServiceRequest,
  cancelServiceRequest,
  assignServiceRequest,
  getDashboardKpis,
} from "../../services/serviceRequests";

// Requester self-cancel is only ever valid pre-review — matches the
// backend's own guard (409 outside backlog/ready).
const CANCELLABLE_STATUSES = ["backlog", "ready"];

const isFilled = (v) => !(v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0));

// ServiceRequest.date/.time are plain display strings on the model (see the
// New Request drawer), so editing them means round-tripping through the
// control's own value shape and back to that same format.
const dateToInput = (s) => {
  if (!s) return null;
  const d = dayjs(s);
  return d.isValid() ? d.toDate() : null;
};
const dateToPayload = (d) => (d ? dayjs(d).format("MMM D, YYYY") : "");
const timeToInput = (t) => {
  if (!t) return "";
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(String(t).trim());
  if (!m) return "";
  let h = Number(m[1]);
  const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
};
const timeToPayload = (t) => {
  if (!t) return "";
  const parsed = dayjs(`2000-01-01T${t}`);
  return parsed.isValid() ? parsed.format("h:mm A") : t;
};

const useStyles = createStyles(() => ({
  drawerContent: {
    borderRadius: "16px 0 0 16px",
    "@media (max-width: 768px)": { borderRadius: 0 },
  },
  // Mantine leaves the drawer's body at auto height, so the `height: 100%` on
  // .root below had nothing to resolve against and the panel collapsed to its
  // content — which parked the loader at the top instead of the middle.
  drawerBody: { height: "100%" },
  root: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, background: "var(--app-bg)" },
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16,
    padding: "20px 24px 16px", borderBottom: "1px solid var(--app-border)", flexShrink: 0, background: "var(--app-surface)",
    "@media (max-width: 768px)": { padding: "14px 14px 12px", gap: 10 },
  },
  eyebrow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 7 },
  typeTag: {
    background: "var(--app-surface-2)", border: "1px solid var(--app-border)", color: "var(--app-text)",
    fontWeight: 600, padding: "2px 10px", borderRadius: 999, fontSize: 11.5,
  },
  reqTitle: { fontSize: 20, fontWeight: 700, color: "var(--app-text)", letterSpacing: "-0.015em", "@media (max-width: 768px)": { fontSize: 17 } },
  // Editable stand-in for reqTitle — same size/weight, but reads as plain
  // heading text until touched (border/background only show on hover/focus)
  // so Title isn't ALSO a separate field further down the same screen.
  reqTitleInput: {
    fontSize: 20, fontWeight: 700, color: "var(--app-text)", letterSpacing: "-0.015em",
    "@media (max-width: 768px)": { fontSize: 17 },
    height: "auto", minHeight: "unset", border: "1px solid transparent", borderRadius: 6,
    padding: "1px 6px", margin: "-1px -6px",
    "&:hover": { background: "var(--app-surface-2)", borderColor: "var(--app-border)" },
    "&:focus": { borderColor: "var(--app-primary)", background: "var(--app-surface)" },
  },
  closeBtn: { width: 30, height: 30, borderRadius: 999, background: "var(--app-surface-2)", color: "var(--app-text)", flexShrink: 0 },
  primaryBtn: { backgroundColor: "var(--app-primary)", color: "var(--app-on-primary)", "&:hover": { backgroundColor: "var(--app-primary) !important", opacity: 0.85 } },
  dangerBtn: { color: "#DC2626", borderColor: "#FECACA", "&:hover": { background: "var(--app-tint-red)" } },
  loaderWrap: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center" },
  // Matches the mockup's `.rv-caret` — a plain down chevron, muted until
  // the field is actually touched.
  railCaret: { color: "var(--app-text-muted)", opacity: 0.55, flexShrink: 0 },
  headerMain: { minWidth: 0 },
  cardHeadRight: { display: "flex", alignItems: "center", gap: 10 },
  editActions: { display: "flex", gap: 10},
  decideActions: { display: "flex", gap: 10 },
  sigImg: { maxWidth: 140, maxHeight: 44, objectFit: "contain" },
  railKRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 },
  // Mockup `.rail-approve`: a quiet tinted pill at rest — the border is the
  // pale accent *ring*, not full-strength amber, so it doesn't outshout the
  // status value it sits above. Solid amber is the hover state. There's no
  // ring token in the app, so it's expressed as a translucent primary, the
  // same way this file already writes its focus rings.
  railApprovePill: {
    display: "inline-flex", alignItems: "center", gap: 5, height: 28, padding: "0 12px", borderRadius: 999,
    border: "1px solid rgba(255, 186, 69, 0.45)", background: "var(--app-tint-amber)", color: "var(--app-ink-amber)",
    fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0, fontFamily: "Inter",
    "&:hover": { background: "var(--app-primary)", borderColor: "var(--app-primary)", color: "var(--app-text)" },
  },
  soHead: { padding: "18px 22px 4px", "@media (max-width: 560px)": { padding: "16px 16px 4px" } },
  soTitle: { fontSize: 17, fontWeight: 700, color: "var(--app-text)", letterSpacing: "-0.01em" },
  soSub: { fontSize: 12.5, color: "var(--app-text-muted)", marginTop: 4, lineHeight: 1.4 },
  soBody: { padding: "14px 22px 6px", "@media (max-width: 560px)": { padding: "12px 16px 6px" } },
  soGrid: { display: "flex", flexDirection: "column", border: "1px solid var(--app-border)", borderRadius: 10, overflow: "hidden", marginBottom: 16 },
  // "Approver / <name>" and "Date & Time / <stamp>" are key-value pairs — they
  // wrap to two lines rather than letting the value overflow its row.
  soRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "11px 14px",
    "&:not(:first-of-type)": { borderTop: "1px solid var(--app-border)" },
    "@media (max-width: 480px)": { alignItems: "flex-start", flexDirection: "column", gap: 4 },
  },
  soK: { fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--app-text-muted)" },
  soV: { fontSize: 13.5, fontWeight: 600, color: "var(--app-text)" },
  soRoleTag: { fontSize: 11, fontWeight: 600, color: "var(--app-text-muted)", marginLeft: 5 },
  soSigLabel: { fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--app-text-muted)", marginBottom: 6 },
  soSigBox: {
    border: "1px solid var(--app-border)", borderRadius: 10, background: "var(--app-surface-2)", height: 84,
    display: "flex", alignItems: "center", justifyContent: "center", borderBottom: "2px solid var(--app-text)",
  },
  soSigName: { fontFamily: "'Snell Roundhand','Brush Script MT','Segoe Script',cursive", fontSize: 34, color: "var(--app-text)" },
  soSigImg: { maxWidth: "90%", maxHeight: 64, objectFit: "contain" },
  soSigEmpty: { fontSize: 12.5, color: "var(--app-text-muted)" },
  soConfirmBody: { alignItems: "flex-start", gap: 9, marginTop: 14 },
  soConfirmLabel: { fontSize: 12.5, color: "var(--app-text)", lineHeight: 1.45, cursor: "pointer" },
  soConfirmLabelErr: { color: "#DC2626" },
  soFoot: {
    padding: "16px 22px", display: "flex", justifyContent: "flex-end", gap: 8,
    "@media (max-width: 480px)": { padding: "14px 16px", "& > button": { flex: 1 } },
  },

  body: { flex: 1, overflowY: "auto", padding: 16, "@media (max-width: 768px)": { padding: 10 } },
  // Mockup `.det-top`: `align-items: stretch`, not start — the pair of cards
  // matches heights so the shorter one doesn't leave a ragged step beside the
  // rail.
  topGrid: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: 12,
    alignItems: "stretch",
    // The 1fr Details rail is a list of label/value rows that can't survive
    // being a third of a phone's width, so below a tablet the two cards stack:
    // Request Information first, Details under it.
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  // `.det-top .det-card { display:flex; flex-direction:column }` +
  // `.det-card-body { flex: 1 }`: whichever card is shorter has its body
  // absorb the slack, which is what lets Description grow to the bottom of
  // the Request Information card instead of leaving dead space under it.
  topCard: { display: "flex", flexDirection: "column" },
  topCardBody: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 },
  // Same exact layout for a decided/terminal request — just non-interactive,
  // instead of swapping in a different read-only structure.
  topGridLocked: { pointerEvents: "none", opacity: 0.7 },
  card: {
    background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)", overflow: "hidden", marginTop: 12,
  },
  cardFirst: { marginTop: 0 },
  railCard: { background: "var(--app-surface-2)" },
  cardHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
    padding: "11px 16px", borderBottom: "1px solid var(--app-border-soft)",
    fontSize: 11, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--app-text-muted)",
    // "Request Information" + request number + a Cancel Request button is more
    // than one phone row holds.
    "@media (max-width: 560px)": { flexWrap: "wrap", padding: "10px 12px" },
  },
  cardHeadKey: { fontWeight: 700, letterSpacing: ".01em", textTransform: "none", color: "var(--app-text)", fontSize: 11.5 },
  dchMeta: { fontWeight: 600, letterSpacing: ".02em", textTransform: "none", fontSize: 11.5, color: "var(--app-text-muted)" },
  cardBody: { padding: "14px 16px 16px", "@media (max-width: 768px)": { padding: "12px 12px 14px" } },

  rfGrid: { display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: "16px 18px" },
  // The editable twin of rfGrid, and the whole reason this block is long:
  // Request Information's fields are a different control spec from the rail's,
  // but both render through the same shared "inline" chrome, which is tuned
  // for the rail. So the card's spec gets restated here —
  // `.rf-k` 11px + `.rq-ctl` 14px/40px/full-width against `.rail-k` 10px +
  // `.rail-v` 13.5px/26px/fit-content.
  //
  // MultiSelect is excluded throughout: its chip tray keeps a real bordered
  // box (`.rms-box`) sized by padding of its own, and would be wrecked by a
  // fixed height. That exclusion is also what lets the Select and Textarea
  // rules match the base rule's specificity and win on source order.
  rfEditGrid: {
    display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: "16px 18px", alignItems: "start",
    // `.rf-k`
    "& .mantine-InputWrapper-label": { fontSize: 11, marginBottom: 6 },
    // `.rq-ctl { width: 100% }`: text entry fills its cell here, unlike the
    // rail, where every value shrink-wraps. All three of Mantine's boxes are
    // fit-content in the shared chrome, so all three widen back.
    "& .mantine-InputWrapper-root, & .mantine-Input-wrapper, & .mantine-Input-input": { width: "100%" },
    "& .mantine-InputWrapper-root": { display: "flex", flexDirection: "column", alignItems: "stretch" },
    // `.rq-ctl` at rest: no box, inset 8px to the left so the value lines up
    // flush under its label instead of stepping in from it. The box itself
    // only appears on hover/focus, inherited from the shared chrome.
    "& .mantine-Input-input:not(.mantine-MultiSelect-input)": {
      fontSize: 14, fontWeight: 500, lineHeight: 1.5, borderRadius: 8,
      height: 40, minHeight: 40, padding: "0 12px 0 8px", margin: "0 0 0 -8px",
    },
    // `.rf-field .dd-btn`: the one control the mockup keeps fit-content and
    // shorter than the text inputs beside it, with no right padding to
    // reserve. Its `:not` matches the specificity of the rule above so this
    // one wins on source order.
    "& .mantine-Select-root": { display: "inline-flex", width: "auto", alignItems: "flex-start" },
    "& .mantine-Select-wrapper": { width: "auto" },
    "& .mantine-Select-input:not(.mantine-MultiSelect-input)": {
      width: "auto", height: 34, minHeight: 34, padding: "0 8px",
    },
    // `#drawer-detail textarea.rq-ctl` — grows instead of centering one line.
    "& .mantine-Textarea-input:not(.mantine-MultiSelect-input)": {
      height: "auto", minHeight: 78, padding: "9px 12px 9px 8px",
    },
  },
  rfField: { minWidth: 0 },
  // Two fields to a row is the desktop reading; at phone width each half is too
  // narrow for a date or a category name, so every field claims the full row.
  rfSpan6: { gridColumn: "span 6", "@media (max-width: 560px)": { gridColumn: "1 / -1" } },
  rfSpan12: { gridColumn: "1 / -1" },
  rfKey: { fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--app-text-muted)", marginBottom: 6 },
  rfValue: { fontSize: 14, fontWeight: 500, color: "var(--app-text)", lineHeight: 1.5, wordBreak: "break-word", whiteSpace: "pre-wrap" },

  // Mockup `.rf-desc`: sits 18px under the field grid and claims the card's
  // remaining height, so the description box ends flush with the bottom of
  // the card rather than floating with empty space below it.
  rfDesc: { marginTop: 18, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 },
  // Mockup `.desc-read-wrap`: description reads inside a bordered box with
  // its attached photos in a strip underneath.
  descBox: {
    flex: 1, display: "flex", flexDirection: "column", minHeight: 120,
    border: "1px solid var(--app-border)", borderRadius: 10, background: "var(--app-surface)", overflow: "hidden",
    "&:hover": { borderColor: "var(--app-text-muted)" },
    "&:focus-within": { borderColor: "var(--app-primary)", boxShadow: "0 0 0 3px rgba(255, 175, 74, 0.2)" },
  },
  descText: { padding: "12px 14px", fontSize: 14, lineHeight: 1.55, color: "var(--app-text)", whiteSpace: "pre-wrap" },
  // Editable stand-in for descText — a real Textarea, but borderless/
  // background-less so the surrounding descBox is the only visible edge.
  descInput: { border: "none", background: "transparent", padding: "12px 14px", fontSize: 14, lineHeight: 1.55, color: "var(--app-text)" },
  descEmpty: { color: "var(--app-text-muted)" },
  // Attached photos are evidence — a 58px chip was too small to tell one
  // dented door from another, so the strip lays them out on a grid that fills
  // the card's width and each opens full size in the viewer below.
  descPhotos: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 10,
    padding: "12px 14px", borderTop: "1px solid var(--app-border-soft)",
    // Two thumbs a row instead of one oversized one on a phone.
    "@media (max-width: 560px)": { gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8, padding: "10px 12px" },
  },
  photoThumb: {
    height: 110, borderRadius: 8, border: "1px solid var(--app-border)", overflow: "hidden",
    "@media (max-width: 560px)": { height: 92 },
    display: "block", padding: 0, background: "var(--app-surface-2)", cursor: "zoom-in",
    transition: "border-color .12s ease, box-shadow .12s ease",
    "&:hover": { borderColor: "var(--app-primary)", boxShadow: "0 4px 14px rgba(16,24,40,.12)" },
  },
  photoImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

  viewerBody: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
  viewerImg: { maxWidth: "100%", maxHeight: "78vh", objectFit: "contain", display: "block", borderRadius: 8 },

  railField: { padding: "7px 0", borderTop: "1px solid var(--app-border-soft)" },
  railFieldFirst: { borderTop: 0, paddingTop: 0 },
  // Mockup `.rail-field`: the Details column is a list of hairline-divided
  // rows — 7px of vertical padding each, no rule above the first — not a
  // stack of free-floating form controls. Applied to whatever direct children
  // the rail ends up with rather than per row, because several are
  // conditional and the template Client/Staff/Facility fields arrive already
  // wrapped in a Box of their own (see ServiceRequestFields).
  railRows: {
    // `--app-border-soft`, not `--app-border`: these divide rows *within* a
    // card, so the mockup uses its faintest line (`--line-soft`) here. The
    // card's own edge keeps the harder one.
    "& > *": { padding: "7px 0", borderTop: "1px solid var(--app-border-soft)" },
    "& > *:first-child": { borderTop: 0, paddingTop: 0 },
    // Row padding is the only thing setting the rail's rhythm, so nothing
    // inside a row may add margin of its own — Mantine's wrapper otherwise
    // contributes a bottom margin and the rows drift apart unevenly.
    "& .mantine-InputWrapper-root": { marginBottom: 0 },
    // `.rail-v` is 13px/500. The shared inline chrome runs 13.5px/600, which
    // is a hair too loud next to a 10px label at this width.
    "& .mantine-Input-input": { fontSize: 13, fontWeight: 500 },
    // Mockup `#drawer-detail .rms-chip`: the rail's chips are neutral squared
    // tags. The shared chrome supplies the New drawer's amber pill instead
    // (16px radius, brand tint), which is right there and wrong here.
    "& .mantine-MultiSelect-values": { gap: 6 },
    "& .mantine-MultiSelect-value, & .mantine-MultiSelect-defaultValue": {
      height: "auto", borderRadius: 6, padding: "3px 5px 3px 9px", fontSize: 12.5,
      background: "var(--app-surface-2)", border: "1px solid var(--app-border)", color: "var(--app-text)",
    },
    "& .mantine-MultiSelect-defaultValueRemove": { color: "var(--app-text-muted)" },
  },
  // Status is the one rail row carrying a trailing action — the mockup's
  // `.rail-k-row` sits Approve hard against the label's right edge — so its
  // control spans the row instead of shrink-wrapping like the inline fields
  // below it, which is what gives `justify-content: space-between` something
  // to push against.
  railRowFull: {
    "& > .mantine-Select-root": { display: "flex", width: "100%", alignItems: "stretch" },
  },
  railKey: { fontSize: 10, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--app-text-muted)", marginBottom: 4 },
  railValue: { fontSize: 13, color: "var(--app-text)", fontWeight: 500, lineHeight: 1.35 },
  railWhen: { color: "var(--app-text-muted)", fontWeight: 500 },
  statusPill: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
    textTransform: "uppercase", padding: "4px 12px", borderRadius: 999, border: "1px solid var(--app-border)",
    background: "var(--app-surface-2)", color: "var(--app-text)", whiteSpace: "nowrap",
  },
  // Status and Assignee both save the instant they're picked, and both keep
  // showing their OLD value until the response patches the record — so without
  // a marker the control looks like it ignored the pick. These three give it
  // one: the field dims and a small spinner sits over it until the save lands.
  savingFieldWrap: { position: "relative" },
  savingFieldBusy: { opacity: 0.4, pointerEvents: "none" },
  // Just the spinner — no backdrop or caption of its own. The faded field
  // underneath is what keeps it legible on either theme.
  // Anchored to the bottom of the field rather than its centre: the wrapper
  // spans label + control, and the spinner belongs over the control. The 4px
  // lift centres it in either input height these fields use (26 inline, 30 on
  // the rail).
  savingFieldOverlay: {
    position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", justifyContent: "center",
    paddingBottom: 4, zIndex: 3,
  },
  // Status is the one rail value that is editable in place (a stage move),
  // so it reads as a control rather than a pill — sized down to sit in the
  // rail's rhythm instead of the full-height field chrome of the edit form.
  railSelectInput: {
    height: 30, minHeight: 30, fontFamily: "Inter", fontSize: 12.5, fontWeight: 600, lineHeight: "28px",
    background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 8,
    color: "var(--app-text)", paddingLeft: 10, paddingRight: 26,
    "&:focus, &:focus-within": { borderColor: "var(--app-primary)" },
  },
  // mockup priority display is a monochrome 3-bar dot indicator (.prio-ic .pd),
  // not a per-priority color swatch — filled ink dots up to the priority rank.
  railPrio: { display: "inline-flex", alignItems: "center", gap: 7 },
  prioIcRow: { display: "inline-flex", alignItems: "center", gap: 3 },
  prioDot: { width: 6, height: 6, borderRadius: "50%", background: "transparent", border: "1.5px solid var(--app-text-muted)", boxSizing: "border-box", flexShrink: 0 },
  prioDotOn: { background: "var(--app-text)", borderColor: "var(--app-text)" },

  // Name on the left, signature on the right — until the 180px signature line
  // and a full name stop fitting on one phone row, where the mark moves under
  // the name and spans the card instead of being squeezed.
  sigRow: {
    display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 18, padding: "14px 0", borderTop: "1px solid var(--app-border-soft)",
    "@media (max-width: 560px)": { alignItems: "stretch", flexDirection: "column", gap: 10 },
  },
  sigRowFirst: { borderTop: 0, paddingTop: 2 },
  sigInfo: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  sigName: { fontSize: 13.5, fontWeight: 700, color: "var(--app-text)" },
  sigSub: { fontSize: 11.5, color: "var(--app-text-muted)" },
  sigRole: { fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" },
  sigMark: {
    minWidth: 180, borderBottom: "1.5px solid var(--app-text)", textAlign: "center", paddingBottom: 1,
    "@media (max-width: 560px)": { minWidth: 0, width: "100%" },
  },
  sigCursive: { fontFamily: "'Snell Roundhand','Brush Script MT','Segoe Script',cursive", fontSize: 26, color: "var(--app-text)", lineHeight: 1.1 },
  sigAwait: { fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--app-text-muted)" },

  actTabs: { display: "flex", gap: 20, borderBottom: "1px solid var(--app-border)" },
  actTab: {
    border: 0, background: "transparent", fontFamily: "Inter", fontSize: 13.5, fontWeight: 600, color: "var(--app-text-muted)",
    padding: "10px 2px", cursor: "pointer", position: "relative", textTransform: "none", letterSpacing: 0,
  },
  actTabOn: {
    color: "var(--app-text)",
    "&::after": { content: '""', position: "absolute", left: 0, right: 0, bottom: -1, height: 2, background: "var(--app-text)", borderRadius: 2 },
  },
  actEmpty: { fontSize: 12.5, color: "var(--app-text-muted)", padding: "6px 0 2px" },

  // Mockup `.composer` / `.cmt2`: avatar + bubble, newest at the bottom.
  avatar: {
    width: 30, height: 30, borderRadius: "50%", background: "var(--app-surface-2)", color: "var(--app-text)",
    display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 2,
    overflow: "hidden",
  },
  avatarMe: { background: "var(--app-primary)", color: "var(--app-on-primary)" },
  avatarPhoto: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  composer: { display: "flex", gap: 11, alignItems: "flex-start", marginBottom: 18 },
  composerBox: { flex: 1, minWidth: 0 },
  composerActions: { display: "flex", justifyContent: "flex-end", marginTop: 8 },
  cmtList: { display: "flex", flexDirection: "column", gap: 14 },
  cmt: { display: "flex", gap: 11 },
  cmtBody: {
    flex: 1, minWidth: 0, background: "var(--app-surface-2)", border: "1px solid var(--app-border)",
    borderRadius: 12, borderTopLeftRadius: 4, padding: "10px 14px",
  },
  cmtHead: { display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 },
  cmtWho: { fontWeight: 700, fontSize: 13, color: "var(--app-text)" },
  cmtWhen: { fontSize: 11.5, color: "var(--app-text-muted)" },
  cmtText: { fontSize: 13.5, color: "var(--app-text)", lineHeight: 1.5, whiteSpace: "pre-wrap" },

  histItem: { display: "flex", gap: 12, position: "relative", paddingBottom: 18 },
  histItemLast: { paddingBottom: 0 },
  histConnector: {
    content: '""', position: "absolute", left: 12, top: 27, bottom: -2, width: 2, background: "var(--app-border)",
  },
  histIc: {
    width: 26, height: 26, borderRadius: "50%", background: "var(--app-surface-2)", border: "1px solid var(--app-border)",
    color: "var(--app-text)", display: "grid", placeItems: "center", flexShrink: 0, zIndex: 1,
  },
  histIcDone: { background: "var(--app-text)", color: "var(--app-surface)", borderColor: "var(--app-text)" },
  histText: { fontSize: 13, color: "var(--app-text)", lineHeight: 1.45 },
  histWhen: { fontSize: 11.5, color: "var(--app-text-muted)", marginTop: 2 },

  span6: { gridColumn: "span 6", "@media (max-width: 560px)": { gridColumn: "1 / -1" } },
}));

// Mockup's priority indicator is a monochrome 3-dot rank bar (Low=1, Medium=2,
// High=3 filled dots), not a per-priority color.
const PRIORITY_RANK = { Low: 1, Medium: 2, High: 3 };

// History entry icon per logged activity type — the shape the mockup's
// timeline (TLIC) uses, mapped onto the backend's `activity.type` values.
const ACTIVITY_ICON = {
  created: IconPlus,
  moved: IconArrowRight,
  updated: IconPencil,
  assigned: IconUser,
  approved: IconCheck,
  rejected: IconX,
  cancelled: IconBan,
};

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

// A commenter's own photo where they have one, their initials where they
// don't. User.photo is a plain URL (unlike the AES-encrypted name fields
// elsewhere on the profile), but it can outlive the file it points at — a
// broken one falls back to the initials rather than leaving an empty disc.
const CommentAvatar = ({ classes, cx, photo, name, me }) => {
  const [broken, setBroken] = useState(false);
  return (
    <Box className={cx(classes.avatar, me && classes.avatarMe)}>
      {photo && !broken
        ? <img src={photo} alt="" className={classes.avatarPhoto} onError={() => setBroken(true)} />
        : initials(name)}
    </Box>
  );
};

const RailField = ({ classes, cx, label, value, first, action }) => (
  <Box className={cx(classes.railField, first && classes.railFieldFirst)}>
    {action ? (
      <Box className={classes.railKRow}>
        <Text className={classes.railKey} mb={0}>{label}</Text>
        {action}
      </Box>
    ) : (
      <Text className={classes.railKey}>{label}</Text>
    )}
    <Box className={classes.railValue}>{value}</Box>
  </Box>
);

const ReadField = ({ classes, cx, label, value, wide }) => (
  <Box className={cx(classes.rfField, wide ? classes.rfSpan12 : classes.rfSpan6)}>
    <Text className={classes.rfKey}>{label}</Text>
    <Text className={classes.rfValue}>{value || "—"}</Text>
  </Box>
);

// Matches the mockup's dedicated sign-off modal: a lightweight confirmation
// step between clicking Approve and the vote actually landing, showing the
// approver who they are, when, and their actual on-file signature before
// they commit — the same one castApprovalVote snapshots onto the vote.
// Matches the mockup's "Reject request" modal (openRejectModal) — a reason
// is required, the requester is notified with it once confirmed.
const RejectModal = ({ opened, close, classes, btnClass, comment, onCommentChange, onConfirm, loading }) => (
  <Modal opened={opened} onClose={close} withCloseButton={false} centered radius={16} padding={0} size={420} zIndex={1200}>
    <Box className={classes.soHead}>
      <Text className={classes.soTitle}>Reject request</Text>
      <Text className={classes.soSub}>The requester will be notified with your reason.</Text>
    </Box>
    <Box className={classes.soBody}>
      <Textarea
        placeholder="Reason for rejection (required)…"
        value={comment}
        onChange={(e) => onCommentChange(e.currentTarget.value)}
        minRows={3}
        autosize
        mb={16}
      />
    </Box>
    <Box className={classes.soFoot}>
      <Button variant="default" className={btnClass} onClick={close}>Cancel</Button>
      <Button color="red" loading={loading} disabled={!comment.trim()} onClick={onConfirm}>Reject Request</Button>
    </Box>
  </Modal>
);

const SignoffModal = ({ opened, close, classes, cx, btnClass, approverName, approverRole, onConfirm, loading }) => {
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState(false);
  const { signature } = useSelector((state) => state.signature);

  // Same fetch + decrypt pattern as MySignature/TaskReportViewTable — the
  // Signature model's own fields are AES-encrypted client-side, so this is
  // the actual on-file drawn/uploaded signature (or typed text), not a
  // stand-in. Matches what castApprovalVote actually snapshots onto the vote.
  useEffect(() => {
    getSignatureById();
  }, []);
  const sigImage = decrypt(signature?.signature) || decrypt(signature?.signatureImage);

  const confirm = () => {
    if (!agreed) { setErr(true); return; }
    onConfirm();
  };

  return (
    <Modal opened={opened} onClose={close} withCloseButton={false} centered radius={16} padding={0} size={420} zIndex={1200}>
      <Box className={classes.soHead}>
        <Text className={classes.soTitle}>Sign off approval</Text>
        <Text className={classes.soSub}>Your signature approves this request and is recorded on the ticket.</Text>
      </Box>
      <Box className={classes.soBody}>
        <Box className={classes.soGrid}>
          <Box className={classes.soRow}>
            <Text className={classes.soK}>Approver</Text>
            <Text className={classes.soV}>
              {approverName}
              {approverRole && <span className={classes.soRoleTag}>{approverRole}</span>}
            </Text>
          </Box>
          <Box className={classes.soRow}>
            <Text className={classes.soK}>Date &amp; Time</Text>
            <Text className={classes.soV}>{dayjs().format("MMM D, YYYY · h:mm A")}</Text>
          </Box>
        </Box>
        <Text className={classes.soSigLabel}>Signature</Text>
        <Box className={classes.soSigBox}>
          {sigImage ? (
            <img src={sigImage} alt="signature" className={classes.soSigImg} />
          ) : signature?.signatureText ? (
            <Text className={classes.soSigName}>{signature.signatureText}</Text>
          ) : (
            <Text className={classes.soSigEmpty}>No signature on file</Text>
          )}
        </Box>
        <Checkbox
          classNames={{ body: classes.soConfirmBody, label: cx(classes.soConfirmLabel, err && classes.soConfirmLabelErr) }}
          checked={agreed}
          onChange={(e) => { setAgreed(e.currentTarget.checked); setErr(false); }}
          label="I confirm this is my electronic signature approving this request."
        />
      </Box>
      <Box className={classes.soFoot}>
        <Button variant="default" className={btnClass} onClick={close}>Cancel</Button>
        <Button variant="filled" loading={loading} onClick={confirm} className={classes.primaryBtn} leftIcon={<IconCheck size={14} stroke={2.5} />}>
          Confirm &amp; sign
        </Button>
      </Box>
    </Modal>
  );
};

const ServiceRequestDetailDrawer = ({ opened, requestId, close }) => {
  const { classes, cx } = useStyles();
  // Full-viewport on a phone: the drawer holds two stacked cards, a signature
  // list and the comment thread, none of which have width to spare.
  const isMobile = useMediaQuery("(max-width: 768px)", undefined, { getInitialValueInEffect: false });
  const btnClass = useSrButtonClassNames();
  const fieldClassNames = useSrFieldClassNames();
  // Every field in the always-editable Details/Request Information cards
  // uses this instead: matches the mockup's rail fields (plain label+value
  // text until touched), not a form's bordered inputs.
  const inlineFieldClassNames = useSrFieldClassNames("inline");
  const { profile } = useSelector((state) => state.profile);
  const { selectedRequest, selectedRequestLoading, templates, issueCategories, settings, boards } = useSelector((state) => state.serviceRequests);
  const { allStaff } = useSelector((state) => state.staff);
  // A template's own Client/Staff-type field stores a bare Patient/User id in
  // req.fieldValues — unlike the built-in clients/staff arrays, nothing on
  // the backend populates it, so resolving it to a name needs this directory
  // client-side (same one ServiceRequestFields' own useDirectories fetches
  // for the editable pickers). Names come back already decrypted.
  const allPatients = useSelector((state) => state.patient?.allPatients) || [];
  useEffect(() => {
    if (!allPatients.length) getAllPatientsModal("all", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [editValues, setEditValues] = useState(null);
  const [editFieldValues, setEditFieldValues] = useState(null);
  const [syncedRequestId, setSyncedRequestId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [signoffOpen, setSignoffOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [assigneeSaving, setAssigneeSaving] = useState(false);
  const [activityTab, setActivityTab] = useState("comments");
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (opened && requestId) {
      getServiceRequestById(requestId);
      setRejectOpen(false);
      setRejectComment("");
      setActivityTab("comments");
      setCommentText("");
      setPhotoUrl(null);
    }
  }, [opened, requestId]);

  const req = selectedRequest;

  // Every field is editable in place — there's no separate "Edit" mode to
  // step into, so editValues has to be seeded as soon as a request loads
  // rather than on a button click. Adjusting state during render (guarded,
  // as React's docs describe) instead of a useEffect avoids a one-frame
  // flash of stale values before the sync effect would otherwise run.
  if (req && req.id !== syncedRequestId) {
    setSyncedRequestId(req.id);
    setEditValues({
      title: req.title || "",
      priority: req.priority || "Medium",
      issueCategory: refId(req.issueCategory) || "",
      facility: refId(req.facility) || "",
      clients: (req.clients || []).map(refId),
      staff: (req.staff || []).map(refId),
      shift: req.shift || "",
      date: dateToInput(req.date),
      time: timeToInput(req.time),
      description: req.description || "",
      photos: req.photos || [],
    });
    setEditFieldValues({ ...(req.fieldValues || {}) });
  }

  const currentUserId = profile?.user?.id || profile?.user?._id;
  const admin = isServiceRequestAdmin(profile);
  // Plain staff accounts only — not contractors, not admins — lose the
  // internal Comments/History activity section per the staff-view ticket.
  // Deliberately narrower than `!admin` so contractors keep seeing it.
  const isPlainStaff = profile?.user?.role === "staff" && profile?.user?.type === "staff";

  // Assignee picker's roster — fetched lazily since only admins can reassign
  // and every other Service Requests screen that needs the staff directory
  // (e.g. ServiceRequestTemplateBuilder) fetches it the same way. The board
  // comes along for the status picker: its columns ARE the stages a request
  // can sit in, and the staff view never loads it on its own.
  useEffect(() => {
    if (!opened || !admin) return;
    getAllStaff("all", false);
    if (!boards.length) getBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, admin]);

  // Only active staff can be assigned work. The strict `=== true` matters
  // here: redux `allStaff` holds the /staff/all response's `allStaff`, which
  // the backend builds from its `deletedInclude` query — every User under the
  // org, with no type/active/deleted filter of its own — so this list is the
  // only guard on who's offered. `active !== false` let anything through whose
  // flag was missing rather than explicitly true; `=== true` requires the
  // confirmed-active record, same test AddAttendanceModal's roster uses.
  // (Refetching from the server's own `activeStaff` page isn't an option —
  // that response carries no `allStaff` key, so it would blank the directory
  // for every other screen reading this slice.)
  const isAssignableStaff = (s) => s?.active === true && s?.deleted !== true && s?.platform === "ogiso";
  const assigneeOptions = [
    { value: "", label: "Unassigned" },
    ...(allStaff || [])
      .filter(isAssignableStaff)
      .map((s) => ({ value: String(s.id || s._id), label: displayRef(s) })),
  ];
  // Whoever the request is already assigned to stays listed even once they're
  // deactivated — Select renders a value it has no option for as blank, which
  // would read as "unassigned" and quietly lose who actually owns the work.
  // They just can't be newly picked, since they're no longer in the roster.
  const assignedToId = refId(req?.assignedTo);
  if (assignedToId && !assigneeOptions.some((o) => o.value === String(assignedToId))) {
    assigneeOptions.push({ value: String(assignedToId), label: displayRef(req.assignedTo) });
  }

  // Only genuinely decided/terminal states make the fields themselves
  // non-interactive (topGridLocked below) — awaiting_approval leaves them
  // visually editable so the Status control can still narrow down to
  // "current stage or a decision" (see canChangeStatus/canDecideFromStatus),
  // since that's the one thing the approval vote actually governs and it
  // lives in the same pointer-events-gated grid as everything else.
  const locked = ["approved", "rejected", "cancelled"].includes(req?.status);
  // The backend's own lock (serviceRequest.service.js's updateServiceRequest)
  // rejects ANY edit while a request sits in its board's approval-gate
  // column — awaiting_approval is included there even though it isn't in
  // `locked` above. Save Changes has to know that split: it can never
  // succeed in this state, so it's disabled here specifically, distinct
  // from the terminal `locked` case (which already disables it too).
  const awaitingApproval = req?.status === "awaiting_approval";
  const template = req?.template && typeof req.template === "object" && req.template.fields
    ? req.template
    : templates.find((t) => t.id === refId(req?.template));
  const issueCategory = req ? issueCategories.find((c) => c.id === refId(req.issueCategory)) : null;

  // Mirrors the New Request drawer's own templateDefinesForm/
  // templateFacilityFieldId: a template with its own fields replaces the
  // generic Title/Priority/Issue Category/Date/Time/Description/Photos/
  // Client(s)/Staff/Shift fields entirely (they were never collected, so
  // there's nothing to edit) — only Facility still needs a home, either the
  // built-in field or the template's own Facility-type field, never both.
  const templateDefinesForm = !!template && !template.isDefault && template.fields?.length > 0;
  // A template field only counts as "handling" Facility/Client/Staff if it's
  // actually reachable given the request's current answers — a field sitting
  // behind a section or field-level condition that isn't met is invisible on
  // the real form, so treating it as handled would hide the built-in
  // fallback and leave the value with nowhere to be edited. Mirrors the New
  // Request drawer's templateFieldReachable.
  const detailConditionCtx = buildConditionContext({
    sections: template?.sections, formFields: template?.fields, values: editFieldValues,
  });
  const detailSectionsById = new Map((template?.sections || []).map((s) => [s.sectionId, s]));
  const templateFieldReachable = (field) => {
    if (!field) return false;
    const section = field.sectionId ? detailSectionsById.get(field.sectionId) : null;
    if (section && !conditionHolds(section.cond, detailConditionCtx)) return false;
    return conditionHolds(field.conditional, detailConditionCtx);
  };
  const rawTemplateFacilityField = (template?.fields || []).find((f) => f.fieldType === "Facility") || null;
  const templateFacilityFieldId = templateFieldReachable(rawTemplateFacilityField) ? rawTemplateFacilityField.fieldId : null;
  const resolvedFacility = templateFacilityFieldId ? editFieldValues?.[templateFacilityFieldId] : editValues?.facility;
  // Client/Staff/Facility-type template fields are the custom-form
  // equivalents of the built-in Details rail's own Client(s)/Staff/Facility
  // — they belong on the right with the rest of that rail, not mixed in
  // with the free-text/choice fields on the left. Each one slots into the
  // SAME position its built-in counterpart holds on a general template
  // (Priority, Facility, Client(s), Staff, Shift) rather than being grouped
  // together whichever order the template author happened to drop them in.
  const RAIL_TEMPLATE_FIELD_TYPES = ["Client", "Staff", "Facility"];
  const railTemplateFields = (template?.fields || []).filter((f) => RAIL_TEMPLATE_FIELD_TYPES.includes(f.fieldType));
  const mainTemplateFields = (template?.fields || []).filter((f) => !RAIL_TEMPLATE_FIELD_TYPES.includes(f.fieldType));
  const rawTemplateClientField = railTemplateFields.find((f) => f.fieldType === "Client") || null;
  const rawTemplateStaffField = railTemplateFields.find((f) => f.fieldType === "Staff") || null;
  const templateClientField = templateFieldReachable(rawTemplateClientField) ? rawTemplateClientField : null;
  const templateStaffField = templateFieldReachable(rawTemplateStaffField) ? rawTemplateStaffField : null;
  // Same reasoning as resolvedFacility: a custom Client/Staff field is the
  // only place that answer is collected, so it has to ride along as the
  // request's real top-level clients/staff too — every other consumer
  // (reports, filters) reads those top-level fields directly.
  const asIdArray = (v) => (Array.isArray(v) ? v : [v]).filter(Boolean);
  const resolvedClients = templateClientField ? asIdArray(editFieldValues?.[templateClientField.fieldId]) : editValues?.clients;
  const resolvedStaff = templateStaffField ? asIdArray(editFieldValues?.[templateStaffField.fieldId]) : editValues?.staff;
  // The read-only (staff) view prints a template's own Client/Staff field
  // straight from req.fieldValues, which holds bare ids — displayRef only
  // makes sense on a populated record, so an id printed through it would
  // just print the id back. Looking it up in the matching directory first
  // is what actually turns it into a name for that view, mirroring what the
  // editable ServiceRequestFields picker already resolves via its options.
  const resolveDirectoryNames = (ids, directory) => {
    const list = asIdArray(ids);
    if (!list.length) return null;
    return list.map((id) => {
      const match = (directory || []).find((x) => refId(x) === String(id));
      return match ? displayRef(match) : String(id);
    }).join(", ");
  };

  // Same reachability flip as the New Request drawer: a conditional
  // section/field can go from unreachable to reachable (or back) as other
  // answers on the request change, swapping which control is "live" for
  // Facility/Client/Staff. Carry whichever side already has an answer into
  // the other so the swap never resets an already-filled value back to
  // "Required".
  useEffect(() => {
    const facilityFieldId = rawTemplateFacilityField?.fieldId || null;
    if (!editValues || !editFieldValues || !facilityFieldId) return;
    const fromField = editFieldValues[facilityFieldId];
    const fromCore = editValues.facility;
    if (!isFilled(fromField) && isFilled(fromCore)) {
      setEditFieldValues((v) => ({ ...v, [facilityFieldId]: fromCore }));
    } else if (!isFilled(fromCore) && isFilled(fromField)) {
      setEditValues((v) => ({ ...v, facility: fromField }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req?.id, rawTemplateFacilityField?.fieldId, templateFacilityFieldId]);

  useEffect(() => {
    const clientFieldId = rawTemplateClientField?.fieldId || null;
    if (!editValues || !editFieldValues || !clientFieldId) return;
    const fromField = asIdArray(editFieldValues[clientFieldId]);
    const fromCore = editValues.clients || [];
    if (!fromField.length && fromCore.length) {
      setEditFieldValues((v) => ({ ...v, [clientFieldId]: fromCore }));
    } else if (!fromCore.length && fromField.length) {
      setEditValues((v) => ({ ...v, clients: fromField }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req?.id, rawTemplateClientField?.fieldId, templateClientField?.fieldId]);

  useEffect(() => {
    const staffFieldId = rawTemplateStaffField?.fieldId || null;
    if (!editValues || !editFieldValues || !staffFieldId) return;
    const fromField = asIdArray(editFieldValues[staffFieldId]);
    const fromCore = editValues.staff || [];
    if (!fromField.length && fromCore.length) {
      setEditFieldValues((v) => ({ ...v, [staffFieldId]: fromCore }));
    } else if (!fromCore.length && fromField.length) {
      setEditValues((v) => ({ ...v, staff: fromField }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req?.id, rawTemplateStaffField?.fieldId, templateStaffField?.fieldId]);

  // Board columns are the request's possible stages: moving a card is what
  // changes its status.
  const board = boards.find((b) => b.id === refId(req?.board)) || boards[0];

  const canCancel = !!req
    && settings.allowRequesterCancel
    && refId(req.submittedBy) === currentUserId
    && CANCELLABLE_STATUSES.includes(req.status);

  const alreadyVoted = (req?.approvals || []).some((a) => refId(a.approver) === currentUserId);
  const canDecide = admin && req?.status === "awaiting_approval" && !alreadyVoted;
  // Rejected is a real board column like any other stage — it just happens
  // to be terminal, so it belongs in the Status control as an option rather
  // than a separate button. Picking it opens the reason capture below
  // instead of a plain move, since the backend only accepts a rejection
  // through the dedicated vote endpoint (see validateTransition).
  const rejectedColumn = (board?.columns || []).find((c) => c.terminalOutcome === "rejected");
  const approvedColumn = (board?.columns || []).find((c) => c.terminalOutcome === "approved");
  const canDecideFromStatus = canDecide && !!(approvedColumn || rejectedColumn);

  // Every board column is a stage the Status control can name, terminal ones
  // included — Approved and Rejected are statuses a request really reaches, so
  // hiding them from the list left it describing only half the lifecycle.
  // They aren't moves though: validateTransition denies a direct move into a
  // terminal column, so picking one hands off to the approve/reject vote (see
  // changeStatus) and is only selectable while that vote is actually open —
  // i.e. this admin hasn't voted yet and the request sits on the approval gate.
  const stageOptions = [...(board?.columns || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((c) => ({ value: c.key, label: c.label, disabled: !!c.isTerminal && !canDecide }));

  // The Status list while a vote is open: the current stage plus whichever
  // terminal outcomes this board defines. Same two decisions the Approve pill
  // and reject flow offer, reachable from the stage picker itself.
  const decisionStatusOptions = [
    { value: req?.columnKey || "", label: STATUS_LABELS[req?.status] || req?.status || "" },
    ...(approvedColumn ? [{ value: approvedColumn.key, label: "Approved" }] : []),
    ...(rejectedColumn ? [{ value: rejectedColumn.key, label: "Rejected" }] : []),
  ];
  // The signoff modal always signs as the current user — "Owner" for the
  // tenant's own Administrator account (no type/role fields of its own),
  // otherwise their staff role, matching ApprovalsModal's same convention.
  const approverName = profile?.user?.name || [profile?.user?.firstName, profile?.user?.lastName].filter(Boolean).join(" ") || "You";
  const approverRole = !profile?.user?.type ? "Owner" : (profile?.user?.role ? profile.user.role.charAt(0).toUpperCase() + profile.user.role.slice(1) : "");
  const approvedCount = (req?.approvals || []).filter((a) => a.decision === "approved").length;

  // Rows for the "Approval Signatures" card. When the template has a fixed
  // approver list, every one of them gets a row — signed/rejected ones show
  // their vote, the rest show "Awaiting signature" — matching the mockup. An
  // unconfigured template (any qualifying admin may vote) has no fixed
  // roster to enumerate as "awaiting", so it falls back to only the votes
  // actually cast, same as before.
  const templateApproverIds = (template?.approvers || []).map(refId).filter(Boolean);
  const sigRows = templateApproverIds.length
    ? templateApproverIds.map((id) => {
        const vote = (req?.approvals || []).find((a) => refId(a.approver) === id);
        const person = vote?.approver || (allStaff || []).find((s) => refId(s) === id) || (id === currentUserId ? profile?.user : null);
        return { id, vote, person };
      })
    : (req?.approvals || []).map((a) => ({ id: refId(a.approver), vote: a, person: a.approver }));

  const onEdit = (key, value) => setEditValues((s) => ({ ...s, [key]: value }));

  // Attributes, stage and assignee are three separate endpoints (each with
  // its own guards and notifications), so one Save fans out to whichever of
  // them actually changed and only then refetches the canonical record.
  const saveEdit = async () => {
    if (busy) return;
    setBusy(true);
    const updated = await updateServiceRequest(req.id, {
      facility: resolvedFacility || undefined,
      clients: resolvedClients,
      staff: resolvedStaff,
      fieldValues: editFieldValues,
      ...(!templateDefinesForm && {
        title: editValues.title,
        priority: editValues.priority,
        issueCategory: editValues.issueCategory || undefined,
        shift: editValues.shift || "",
        date: dateToPayload(editValues.date),
        time: timeToPayload(editValues.time),
        description: editValues.description || "",
        photos: editValues.photos,
      }),
    });
    if (!updated) {
      setBusy(false);
      return;
    }

    await getServiceRequestById(req.id);
    setBusy(false);
  };

  // Status on the read-only rail is editable in place: a status IS the
  // column the request sits in, so changing it is the same stage move the
  // kanban's drag-drop performs. The move response comes back fully
  // populated and patches the open request, so no refetch is needed unless
  // the backend refused the transition.
  const changeStatus = async (nextColumnKey) => {
    if (!nextColumnKey || statusSaving || nextColumnKey === req.columnKey) return;
    // A terminal stage is a decision, not a move — route it to the same
    // sign-off / reject-reason capture the Approve pill and reject flow use,
    // which is what the backend accepts for those two columns.
    if (nextColumnKey === approvedColumn?.key) {
      setSignoffOpen(true);
      return;
    }
    if (nextColumnKey === rejectedColumn?.key) {
      setRejectOpen(true);
      return;
    }
    setStatusSaving(true);
    try {
      await moveServiceRequest(req.id, req.columnKey, nextColumnKey);
    } catch (e) {
      // The move endpoint surfaced its own error (e.g. a denied transition);
      // refetch so the rail falls back to the stage the server still holds.
      await getServiceRequestById(req.id);
    }
    setStatusSaving(false);
  };

  // Same immediate-save treatment as Status — reassigning fires its own
  // notification right away, so it shouldn't wait behind Save Changes.
  const changeAssignee = async (nextAssignedTo) => {
    if (assigneeSaving || (nextAssignedTo || "") === (refId(req.assignedTo) || "")) return;
    setAssigneeSaving(true);
    try {
      await assignServiceRequest(req.id, nextAssignedTo || null);
      // My Queue counts requests assigned to the current user — assigning
      // to/away from yourself here needs to move that KPI too.
      getDashboardKpis();
    } finally {
      setAssigneeSaving(false);
    }
  };

  const canChangeStatus = admin && !locked && !awaitingApproval && stageOptions.length > 0;

  // The Status control while awaiting approval: the current stage plus the two
  // decisions. Either terminal pick opens its capture step (sign-off modal /
  // reason textarea) rather than firing a move.
  const handleDecisionStatusSelect = (value) => {
    if (value === approvedColumn?.key) setSignoffOpen(true);
    else if (value === rejectedColumn?.key) setRejectOpen(true);
  };

  // Approving now opens the Sign off approval modal (matching the mockup)
  // instead of a plain confirm — the actual vote is cast from there once the
  // approver checks the signature-confirmation box.
  const handleApprove = () => setSignoffOpen(true);

  const confirmApprove = () => {
    setBusy(true);
    approveServiceRequest(req.id, "", () => { setBusy(false); setSignoffOpen(false); });
  };

  const submitReject = () => {
    if (!rejectComment.trim()) return;
    setBusy(true);
    rejectServiceRequest(req.id, rejectComment.trim(), () => {
      setBusy(false);
      setRejectOpen(false);
      setRejectComment("");
    });
  };

  const handleCancel = async () => {
    const confirmed = await swal({
      title: "Cancel this request?",
      text: "This can't be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Never mind", "Cancel Request"],
    });
    if (!confirmed) return;
    setBusy(true);
    cancelServiceRequest(req.id, undefined, () => setBusy(false));
  };

  const postComment = async () => {
    const text = commentText.trim();
    if (!text || posting) return;
    setPosting(true);
    const updated = await addServiceRequestComment(req.id, text);
    setPosting(false);
    if (updated) setCommentText("");
  };

  const comments = req?.comments || [];

  // The server keeps an append-only activity log (History tab). Requests
  // created before that log existed have none, so those fall back to what the
  // record itself still proves: when it was raised and how it was voted on.
  const history = !req ? [] : (req.activity || []).length
    ? req.activity.map((a) => ({
      icon: ACTIVITY_ICON[a.type] || IconArrowRight,
      done: ["approved", "created"].includes(a.type),
      text: <><b>{displayRef(a.actor)}</b> {a.message}</>,
      when: a.at ? new Date(a.at).toLocaleString() : "",
    }))
    : [
      { icon: IconSend, done: true, text: <><b>{displayRef(req.submittedBy)}</b> raised this request</>, when: req.submittedAt ? new Date(req.submittedAt).toLocaleString() : "" },
      ...(req.approvals || []).filter((a) => a.decision && a.decision !== "pending").map((a) => ({
        icon: a.decision === "approved" ? IconCheck : IconX,
        done: a.decision === "approved",
        text: <><b>{displayRef(a.approver)}</b> {a.decision === "approved" ? "approved" : "rejected"} this request{a.comment ? ` — "${a.comment}"` : ""}</>,
        when: a.decidedAt ? new Date(a.decidedAt).toLocaleString() : "",
      })),
    ];

  return (
    <>
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      size={isMobile ? "100%" : "clamp(560px, 55vw, 820px)"}
      withCloseButton={false}
      padding={0}
      classNames={{ content: classes.drawerContent, body: classes.drawerBody }}
      overlayProps={{ opacity: 0.55, blur: 3 }}
      zIndex={1000}
    >
      {/* Attached photos open here rather than in a new tab: a raw S3 URL
          loses the request it belongs to, and coming back means re-opening
          the drawer. Sits above the drawer's own z-index. */}
      <Modal
        opened={!!photoUrl}
        onClose={() => setPhotoUrl(null)}
        title="Photo"
        size="auto"
        centered
        zIndex={1100}
      >
        <Box className={classes.viewerBody}>
          <img src={photoUrl} alt="" className={classes.viewerImg} />
          <Button
            component="a"
            href={photoUrl}
            target="_blank"
            rel="noreferrer"
            variant="default"
            size="xs"
            className={btnClass}
          >
            Open original
          </Button>
        </Box>
      </Modal>

      {!opened ? null : selectedRequestLoading || !req ? (
        <Box className={classes.root}>
          <Box className={classes.loaderWrap}>
            <Loader color="var(--app-primary)" />
          </Box>
        </Box>
      ) : (
        <Box className={classes.root}>
          <Box className={classes.header}>
            <Box className={classes.headerMain}>
              <Box className={classes.eyebrow}>
                <span className={classes.typeTag}>{template?.name || "Service Request"}</span>
              </Box>
              {admin && !templateDefinesForm && editValues ? (
                <TextInput
                  aria-label="Title"
                  variant="unstyled"
                  classNames={{ input: classes.reqTitleInput }}
                  value={editValues.title}
                  onChange={(e) => onEdit("title", e.currentTarget.value)}
                  placeholder="Short summary…"
                  disabled={locked}
                />
              ) : (
                <Text className={classes.reqTitle}>{req.title}</Text>
              )}
            </Box>
            <ActionIcon className={classes.closeBtn} onClick={close} aria-label="Close"><IconX size={15} /></ActionIcon>
          </Box>

          <Box className={classes.body}>
            {/* No separate "Edit" mode to step into — every field a request
                can still change is directly editable the moment the drawer
                opens, mirroring the read view's own two-column split (left =
                Request Information, right = Details) so nothing renders
                twice, for every status an admin can see. A decided/terminal
                request (approved/rejected/cancelled) keeps this exact same
                layout — just non-interactive (topGridLocked) instead of
                swapping in a different read-only structure. Only a
                non-admin viewer falls back to the plain read-only view
                below. */}
            {admin && editValues ? (
              <>
                <Box className={cx(classes.topGrid, locked && classes.topGridLocked)}>
                  <Box className={cx(classes.card, classes.cardFirst, classes.topCard)}>
                    <Box className={classes.cardHead}>
                      <span>Request Information</span>
                      <span className={classes.cardHeadKey}>{req.requestNumber}</span>
                    </Box>
                    <Box className={cx(classes.cardBody, classes.topCardBody)}>
                      <Box className={classes.rfEditGrid}>
                        {!templateDefinesForm && (
                          <>
                            <Box className={classes.span6}>
                              <IssueCategoryField value={editValues.issueCategory} onChange={(v) => onEdit("issueCategory", v)} issueCategories={issueCategories} variant="inline" />
                            </Box>
                            <Box className={classes.span6}>
                              <DateField value={editValues.date} onChange={(v) => onEdit("date", v)} variant="inline" />
                            </Box>
                            <Box className={classes.span6}>
                              <TimeField value={editValues.time} onChange={(v) => onEdit("time", v)} variant="inline" />
                            </Box>
                          </>
                        )}
                        {template && (
                          <ServiceRequestFields
                            fields={mainTemplateFields}
                            allFields={template.fields}
                            sections={template.sections}
                            values={editFieldValues}
                            onChange={(fieldId, v) => setEditFieldValues((s) => ({ ...s, [fieldId]: v }))}
                            variant="inline"
                          />
                        )}
                      </Box>
                      {/* Sibling of the grid, not a cell in it — mirrors the
                          mockup's `.rf-grid` + `.rf-desc` pair, which is what
                          lets the description claim the card's leftover
                          height. */}
                      {!templateDefinesForm && (
                        <Box className={classes.rfDesc}>
                          <Text className={classes.rfKey}>Description</Text>
                          <Box className={classes.descBox}>
                            <Textarea
                              variant="unstyled"
                              autosize
                              minRows={4}
                              placeholder="Describe the request…"
                              value={editValues.description}
                              onChange={(e) => onEdit("description", e.currentTarget.value)}
                              classNames={{ input: classes.descInput }}
                            />
                            {!!(editValues.photos || []).length && (
                              <Box className={classes.descPhotos}>
                                {editValues.photos.map((url) => (
                                  <Box
                                    key={url}
                                    component="button"
                                    type="button"
                                    onClick={() => setPhotoUrl(url)}
                                    className={classes.photoThumb}
                                    title="View photo"
                                  >
                                    <img src={url} alt="" className={classes.photoImg} />
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box className={cx(classes.card, classes.cardFirst, classes.railCard, classes.topCard)}>
                    <Box className={classes.cardHead}>Details</Box>
                    <Box className={cx(classes.cardBody, classes.topCardBody)}>
                      <Box className={classes.railRows}>
                        <Box className={cx(classes.railRowFull, classes.savingFieldWrap)}>
                          <Select
                            className={cx(statusSaving && classes.savingFieldBusy)}
                            label={canDecide ? (
                              <Box className={classes.railKRow}>
                                <span>Status</span>
                                <button type="button" className={classes.railApprovePill} onClick={handleApprove}>
                                  <IconCheck size={12} stroke={3} />Approve
                                </button>
                              </Box>
                            ) : "Status"}
                            classNames={inlineFieldClassNames}
                            data={canDecideFromStatus ? decisionStatusOptions : stageOptions}
                            value={req.columnKey || ""}
                            onChange={canDecideFromStatus ? handleDecisionStatusSelect : changeStatus}
                            disabled={statusSaving}
                            // The in-flight spinner is the overlay below, not a
                            // second one swapped in for the caret.
                            rightSection={<IconChevronDown size={14} className={classes.railCaret} />}
                            placeholder="Select stage…"
                          />
                          {statusSaving && (
                            <Box className={classes.savingFieldOverlay}>
                              <Loader size={14} color="var(--app-primary)" />
                            </Box>
                          )}
                        </Box>
                        <Box className={classes.savingFieldWrap}>
                          <Select
                            className={cx(assigneeSaving && classes.savingFieldBusy)}
                            label="Assignee"
                            classNames={inlineFieldClassNames}
                            data={assigneeOptions}
                            value={refId(req.assignedTo) || ""}
                            onChange={changeAssignee}
                            searchable
                            disabled={assigneeSaving}
                            // The in-flight spinner is the overlay below, not a
                            // second one swapped in for the caret.
                            rightSection={<IconChevronDown size={14} className={classes.railCaret} />}
                            placeholder="Unassigned"
                          />
                          {assigneeSaving && (
                            <Box className={classes.savingFieldOverlay}>
                              <Loader size={14} color="var(--app-primary)" />
                            </Box>
                          )}
                        </Box>
                        {!templateDefinesForm && (
                          <Box>
                            <PriorityField value={editValues.priority} onChange={(v) => onEdit("priority", v)} variant="inline" />
                          </Box>
                        )}
                        {templateFacilityFieldId ? (
                          <ServiceRequestFields
                            fields={railTemplateFields.filter((f) => f.fieldId === templateFacilityFieldId)}
                            allFields={template.fields}
                            sections={template.sections}
                            values={editFieldValues}
                            onChange={(fieldId, v) => setEditFieldValues((s) => ({ ...s, [fieldId]: v }))}
                            variant="inline"
                          />
                        ) : (
                          <Box>
                            <FacilityField value={editValues.facility} onChange={(v) => onEdit("facility", v)} clientIds={editValues.clients} variant="inline" />
                          </Box>
                        )}
                        {templateClientField ? (
                          <ServiceRequestFields
                            fields={[templateClientField]}
                            allFields={template.fields}
                            sections={template.sections}
                            values={editFieldValues}
                            onChange={(fieldId, v) => setEditFieldValues((s) => ({ ...s, [fieldId]: v }))}
                            variant="inline"
                          />
                        ) : !templateDefinesForm && (
                          <Box>
                            <ClientsField value={editValues.clients} onChange={(v) => onEdit("clients", v)} variant="inline" />
                          </Box>
                        )}
                        {templateStaffField ? (
                          <ServiceRequestFields
                            fields={[templateStaffField]}
                            allFields={template.fields}
                            sections={template.sections}
                            values={editFieldValues}
                            onChange={(fieldId, v) => setEditFieldValues((s) => ({ ...s, [fieldId]: v }))}
                            variant="inline"
                          />
                        ) : !templateDefinesForm && (
                          <Box>
                            <StaffField value={editValues.staff} onChange={(v) => onEdit("staff", v)} variant="inline" />
                          </Box>
                        )}
                        {!templateDefinesForm && (
                          <Box>
                            <ShiftField value={editValues.shift} onChange={(v) => onEdit("shift", v)} variant="inline" />
                          </Box>
                        )}
                        {/* Reporter is the rail's one fixed value — it belongs
                            inside the same divided list as the rest, so it
                            renders as a plain row here rather than through
                            RailField, which brings its own divider. */}
                        <Box>
                          <Text className={classes.railKey}>Reporter</Text>
                          <Box className={classes.railValue}>
                            {displayRef(req.submittedBy)}{" "}
                            <span className={classes.railWhen}>{req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : ""}</span>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Box className={classes.editActions}>
                    <Button variant="filled" loading={busy} disabled={locked || awaitingApproval} mt={12} onClick={saveEdit} className={classes.primaryBtn}>Save Changes</Button>
                  </Box>
                  {/* The backend 409s any edit in this state (see `locked`
                      above) — disabling the button without saying why just
                      relocates the same dead end, so the reason rides along
                      with it. */}
                  {awaitingApproval && (
                    <Text fz={12.5} c="var(--app-text-muted)" mt={6}>
                      This request is awaiting approval and can't be edited until a decision is made.
                    </Text>
                  )}
                </Box>
              </>
            ) : (
              <Box className={classes.topGrid}>
                <Box className={cx(classes.card, classes.cardFirst, classes.topCard)}>
                  <Box className={classes.cardHead}>
                    <span>Request Information</span>
                    <Box className={classes.cardHeadRight}>
                      <span className={classes.cardHeadKey}>{req.requestNumber}</span>
                      {canCancel && (
                        <Button size="xs" variant="default" color="red" className={classes.dangerBtn} leftIcon={<IconBan size={14} />} onClick={handleCancel} loading={busy}>
                          Cancel Request
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Box className={cx(classes.cardBody, classes.topCardBody)}>
                    <Box className={classes.rfGrid}>
                      {!templateDefinesForm && (
                        <>
                          <ReadField classes={classes} cx={cx} label="Issue Category" value={issueCategory?.name || (req.issueCategory ? displayRef(req.issueCategory) : "")} />
                          <ReadField classes={classes} cx={cx} label="Date" value={req.date} />
                          <ReadField classes={classes} cx={cx} label="Time" value={req.time} />
                        </>
                      )}
                      {mainTemplateFields.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((f) => (
                        // Notifications configures who gets notified, not a value to show — the rest
                        // of mainTemplateFields already excludes Client/Staff/Facility, which render
                        // in the Details rail instead.
                        f.fieldType === "Notifications" ? null : (
                          <ReadField
                            key={f.fieldId}
                            classes={classes}
                            cx={cx}
                            label={f.label}
                            wide={f.fieldType === "TextArea"}
                            value={Array.isArray(req.fieldValues?.[f.fieldId])
                              ? req.fieldValues[f.fieldId].join(", ")
                              : req.fieldValues?.[f.fieldId]?.toString?.()}
                          />
                        )
                      ))}
                    </Box>

                    {/* Description/Photos are the built-in Request fields — a
                        template that defines its own form was never asked
                        for them (see the same gate on the editable side,
                        above), so req.description/.photos are just empty
                        here. Showing this card anyway is exactly the "extra
                        Description field" staff saw that admin's edit view
                        never renders for the same request. */}
                    {!templateDefinesForm && (
                      <Box className={classes.rfDesc}>
                        <Text className={classes.rfKey}>Description</Text>
                        <Box className={classes.descBox}>
                          <Text className={cx(classes.descText, !req.description && classes.descEmpty)}>
                            {req.description || "No description provided."}
                          </Text>
                          {!!(req.photos || []).length && (
                            <Box className={classes.descPhotos}>
                              {req.photos.map((url) => (
                                <Box
                                  key={url}
                                  component="button"
                                  type="button"
                                  onClick={() => setPhotoUrl(url)}
                                  className={classes.photoThumb}
                                  title="View photo"
                                >
                                  <img src={url} alt="" className={classes.photoImg} />
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box className={cx(classes.card, classes.cardFirst, classes.railCard, classes.topCard)}>
                  <Box className={classes.cardHead}>Details</Box>
                  <Box className={cx(classes.cardBody, classes.topCardBody)}>
                    <RailField
                      classes={classes}
                      cx={cx}
                      first
                      label="Status"
                      action={canDecide ? (
                        <button type="button" className={classes.railApprovePill} onClick={handleApprove}>
                          <IconCheck size={12} stroke={3} />Approve
                        </button>
                      ) : undefined}
                      value={canChangeStatus ? (
                        <Box className={classes.savingFieldWrap}>
                          <Select
                            aria-label="Status"
                            className={cx(statusSaving && classes.savingFieldBusy)}
                            classNames={{
                              input: classes.railSelectInput,
                              dropdown: fieldClassNames.dropdown,
                              item: fieldClassNames.item,
                            }}
                            data={stageOptions}
                            value={req.columnKey || ""}
                            onChange={changeStatus}
                            disabled={statusSaving}
                            placeholder="Select stage…"
                            // The rail card clips its overflow, so the dropdown
                            // has to escape it — above the drawer's own z-index.
                            withinPortal
                            zIndex={1100}
                          />
                          {statusSaving && (
                            <Box className={classes.savingFieldOverlay}>
                              <Loader size={14} color="var(--app-primary)" />
                            </Box>
                          )}
                        </Box>
                      ) : canDecideFromStatus ? (
                        <Select
                          aria-label="Status"
                          classNames={{
                            input: classes.railSelectInput,
                            dropdown: fieldClassNames.dropdown,
                            item: fieldClassNames.item,
                          }}
                          data={decisionStatusOptions}
                          value={req.columnKey || ""}
                          onChange={handleDecisionStatusSelect}
                          withinPortal
                          zIndex={1100}
                        />
                      ) : (
                        <span className={classes.statusPill}>{STATUS_LABELS[req.status] || req.status}</span>
                      )}
                    />
                    <RailField
                      classes={classes}
                      cx={cx}
                      label="Assignee"
                      value={req.assignedTo ? displayRef(req.assignedTo) : <Text span c="var(--app-text-muted)">Unassigned</Text>}
                    />
                    {!templateDefinesForm && (
                      <RailField
                        classes={classes}
                        cx={cx}
                        label="Priority"
                        value={(
                          <span className={classes.railPrio}>
                            <span className={classes.prioIcRow}>
                              {[1, 2, 3].map((i) => (
                                <span key={i} className={cx(classes.prioDot, i <= PRIORITY_RANK[req.priority] && classes.prioDotOn)} />
                              ))}
                            </span>
                            {req.priority}
                          </span>
                        )}
                      />
                    )}
                    <RailField classes={classes} cx={cx} label="Facility" value={displayRef(req.facility)} />
                    {templateClientField ? (
                      <RailField
                        classes={classes}
                        cx={cx}
                        label={templateClientField.label}
                        value={resolveDirectoryNames(req.fieldValues?.[templateClientField.fieldId], allPatients)
                          || <Text span c="var(--app-text-muted)">None</Text>}
                      />
                    ) : !templateDefinesForm && (
                      <RailField
                        classes={classes}
                        cx={cx}
                        label="Client(s)"
                        value={(req.clients || []).length ? req.clients.map(clientRefName).join(", ") : <Text span c="var(--app-text-muted)">None</Text>}
                      />
                    )}
                    {templateStaffField ? (
                      <RailField
                        classes={classes}
                        cx={cx}
                        label={templateStaffField.label}
                        value={resolveDirectoryNames(req.fieldValues?.[templateStaffField.fieldId], allStaff)
                          || <Text span c="var(--app-text-muted)">None</Text>}
                      />
                    ) : !templateDefinesForm && (
                      <RailField
                        classes={classes}
                        cx={cx}
                        label="Staff"
                        value={(req.staff || []).length ? req.staff.map(displayRef).join(", ") : <Text span c="var(--app-text-muted)">None</Text>}
                      />
                    )}
                    {!templateDefinesForm && !!req.shift && <RailField classes={classes} cx={cx} label="Shift" value={req.shift} />}
                    {req.status === "rejected" && (
                      <RailField
                        classes={classes}
                        cx={cx}
                        label="Reason"
                        value={(req.approvals || []).find((a) => a.decision === "rejected")?.comment || "—"}
                      />
                    )}
                    {req.status === "cancelled" && !!req.cancelReason && (
                      <RailField classes={classes} cx={cx} label="Reason" value={req.cancelReason} />
                    )}
                    <RailField classes={classes} cx={cx} label="Reporter" value={<>{displayRef(req.submittedBy)} <span className={classes.railWhen}>{req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : ""}</span></>} />
                  </Box>
                </Box>
              </Box>
            )}

            {sigRows.length > 0
              && (["awaiting_approval", "approved", "rejected"].includes(req.status) || (req.approvals || []).length > 0) && (
              <Box className={classes.card}>
                <Box className={classes.cardHead}>
                  <span>Approval Signatures</span>
                  <span className={classes.dchMeta}>{approvedCount} of {req.quorumRequired} signed</span>
                </Box>
                <Box className={classes.cardBody}>
                  {sigRows.map(({ id, vote, person }, i) => {
                    const signed = vote?.decision === "approved";
                    const rejected = vote?.decision === "rejected";
                    const role = !person?.type
                      ? "Owner"
                      : person?.role
                        ? person.role.charAt(0).toUpperCase() + person.role.slice(1)
                        : "";
                    return (
                      <Box key={id || i} className={cx(classes.sigRow, i === 0 && classes.sigRowFirst)}>
                        <Box className={classes.sigInfo}>
                          <Text className={classes.sigName}>{displayRef(person)}</Text>
                          {vote?.decidedAt ? (
                            <Text className={classes.sigSub}>
                              {role && <span className={classes.sigRole}>{role}</span>}
                              {role ? " · " : ""}
                              {dayjs(vote.decidedAt).format("MMM D, YYYY · h:mm A")}
                            </Text>
                          ) : role ? (
                            <Text className={classes.sigSub}><span className={classes.sigRole}>{role}</span></Text>
                          ) : null}
                          {vote?.comment && <Text className={classes.sigSub}>{vote.comment}</Text>}
                        </Box>
                        {signed ? (
                          <Box className={classes.sigMark}>
                            {decrypt(vote.signatureSnapshot?.signature) || decrypt(vote.signatureSnapshot?.signatureImage) ? (
                              <img
                                src={decrypt(vote.signatureSnapshot?.signature) || decrypt(vote.signatureSnapshot?.signatureImage)}
                                alt="signature"
                                className={classes.sigImg}
                              />
                            ) : (
                              <Text className={classes.sigCursive}>{vote.signatureSnapshot?.signatureText || displayRef(person)}</Text>
                            )}
                          </Box>
                        ) : rejected ? (
                          <Text className={classes.sigAwait} c="var(--app-ink-red)">Rejected</Text>
                        ) : (
                          <Text className={classes.sigAwait}>Awaiting signature</Text>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {!isPlainStaff && (
              <Box className={classes.card}>
                <Box className={classes.cardBody}>
                  <Box className={classes.actTabs} mb={18}>
                    <button type="button" className={cx(classes.actTab, activityTab === "comments" && classes.actTabOn)} onClick={() => setActivityTab("comments")}>
                      Comments{comments.length ? ` (${comments.length})` : ""}
                    </button>
                    <button type="button" className={cx(classes.actTab, activityTab === "history" && classes.actTabOn)} onClick={() => setActivityTab("history")}>History</button>
                  </Box>

                  {activityTab === "history" ? (
                    history.length ? (
                      <Box>
                        {history.map((h, i) => (
                          <Box key={i} className={cx(classes.histItem, i === history.length - 1 && classes.histItemLast)}>
                            {i !== history.length - 1 && <Box className={classes.histConnector} />}
                            <Box className={cx(classes.histIc, h.done && classes.histIcDone)}><h.icon size={13} /></Box>
                            <Box>
                              <Text className={classes.histText}>{h.text}</Text>
                              {h.when && <Text className={classes.histWhen}>{h.when}</Text>}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : <Text className={classes.actEmpty}>No activity yet.</Text>
                  ) : (
                    <>
                      <Box className={classes.composer}>
                        <CommentAvatar classes={classes} cx={cx} me photo={profile?.user?.photo} name={profile?.user?.name} />
                        <Box className={classes.composerBox}>
                          <Textarea
                            placeholder="Add a comment…"
                            value={commentText}
                            onChange={(e) => setCommentText(e.currentTarget.value)}
                            classNames={fieldClassNames}
                            minRows={3}
                            autosize
                          />
                          <Box className={classes.composerActions}>
                            <Button variant="filled" size="xs" loading={posting} disabled={!commentText.trim()} onClick={postComment} className={classes.primaryBtn}>
                              Comment
                            </Button>
                          </Box>
                        </Box>
                      </Box>

                      {comments.length ? (
                        <Box className={classes.cmtList}>
                          {comments.map((c, i) => (
                            <Box key={c.id || c._id || i} className={classes.cmt}>
                              <CommentAvatar classes={classes} cx={cx} photo={c.author?.photo} name={displayRef(c.author)} />
                              <Box className={classes.cmtBody}>
                                <Box className={classes.cmtHead}>
                                  <Text className={classes.cmtWho}>{displayRef(c.author)}</Text>
                                  <Text className={classes.cmtWhen}>{c.createdAt ? new Date(c.createdAt).toLocaleString() : ""}</Text>
                                </Box>
                                <Text className={classes.cmtText}>{c.text}</Text>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Text className={classes.actEmpty}>No comments yet. Start the conversation above.</Text>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}

          </Box>
        </Box>
      )}
    </Drawer>
      {signoffOpen && (
        <SignoffModal
          opened={signoffOpen}
          close={() => setSignoffOpen(false)}
          classes={classes}
          cx={cx}
          btnClass={btnClass}
          approverName={approverName}
          approverRole={approverRole}
          onConfirm={confirmApprove}
          loading={busy}
        />
      )}
      {rejectOpen && (
        <RejectModal
          opened={rejectOpen}
          close={() => { setRejectOpen(false); setRejectComment(""); }}
          classes={classes}
          btnClass={btnClass}
          comment={rejectComment}
          onCommentChange={setRejectComment}
          onConfirm={submitReject}
          loading={busy}
        />
      )}
    </>
  );
};

export default ServiceRequestDetailDrawer;
